/**
 * Format a date in the learner's chosen UI language (#4392).
 *
 * `Date#toLocaleDateString()` with no argument reads the BROWSER's locale, so a
 * French-UI learner on an en-US browser got "10/12/2026" (US order) beside a
 * French sentence. Pass the i18n language instead.
 *
 * `lng` is whatever the i18n instance reports (`resolvedLanguage || language`).
 * It can be undefined (an uninitialised or mocked i18n) or not a valid BCP 47
 * tag; in both cases fall back to the browser default rather than throw, because
 * `toLocaleDateString` throws a RangeError on a malformed tag.
 */
/**
 * i18n reports the bare UI code ('en'), which formats US order for every
 * English-UI learner. When the browser's own locale shares that base language
 * (en-ZA, en-GB, ...), keep the browser's regional locale; otherwise use the
 * UI language. Any failure reading `navigator` returns `lng` unchanged.
 */
const withBrowserRegion = (lng) => {
  try {
    const browser = typeof navigator !== 'undefined' ? navigator.language : undefined;
    if (
      typeof browser === 'string' &&
      browser.split(/[-_]/)[0].toLowerCase() === String(lng).split(/[-_]/)[0].toLowerCase() &&
      // only widen a bare UI code; an explicit regional UI tag already wins
      !/[-_]/.test(lng)
    ) {
      return browser;
    }
  } catch {
    // hostile navigator: fall through to lng
  }
  return lng;
};

export const formatDateForLanguage = (date, lng) => {
  if (lng) {
    try {
      return date.toLocaleDateString(withBrowserRegion(lng));
    } catch {
      // malformed tag: fall through to the browser default
    }
  }
  return date.toLocaleDateString();
};
